// components/OrderManagement.js
import React, { useState, useEffect } from 'react';
import { Clock, ChevronDown, ChevronUp, Phone, Mail, DollarSign, ShoppingCart, AlertCircle, CookingPot, CheckCircle, XCircle, TrendingUp, Search, Filter, Download, Calendar, User, Package, Users } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';

const OrderManagement = () => {
    const [orders, setOrders] = useState([]);
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        orderId: null,
        newStatus: null,
        orderNumber: null
    });
    const [isUpdating, setIsUpdating] = useState(false);

    // Filter and search states
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [sortBy, setSortBy] = useState('newest');

    useEffect(() => {
        loadOrders();
        // Set up polling for new orders every 30 seconds
        const interval = setInterval(loadOrders, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadOrders = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASEURL}/api/orders`);
            const data = await response.json();
            setOrders(data);
        } catch (error) {
            console.error('Failed to load orders:', error);
        } finally {
            setLoading(false);
        }
    };
    const handleStatusUpdateClick = (orderId, newStatus, orderNumber) => {
        setConfirmDialog({
            isOpen: true,
            orderId,
            newStatus,
            orderNumber
        });
    };


    const handleStatusUpdate = async () => {
        // Get orderId and newStatus from confirmDialog state
        const { orderId, newStatus } = confirmDialog;
        setIsUpdating(true);
    
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASEURL}/api/stripe/orders/${orderId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: newStatus }),
            });
    
            if (response.ok) {
                // Wait a bit before closing to ensure user sees the loading message
                await loadOrders(); // Refresh orders after status update
                await new Promise(resolve => setTimeout(resolve, 500)); // Add small delay to show loading state
            }
        } catch (error) {
            console.error('Failed to update order status:', error);
        } finally {
            setIsUpdating(false);
            setConfirmDialog({ isOpen: false, orderId: null, newStatus: null, orderNumber: null });
        }
    };

    const getStatusBadgeColor = (status) => {
        const colors = {
            pending: 'bg-gray-100 text-gray-800',
            paid: 'bg-blue-100 text-blue-800',
            preparing: 'bg-yellow-100 text-yellow-800',
            ready: 'bg-green-100 text-green-800',
            completed: 'bg-purple-100 text-purple-800',
            cancelled: 'bg-red-100 text-red-800',
        };
        return colors[status] || colors.pending;
    };

    const formatDate = (dateString) => {
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    // Calculate statistics from orders
    const getOrderStats = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const completedOrders = orders.filter(order => order.status === 'completed');
        const todayOrders = orders.filter(order => new Date(order.createdAt) >= today);

        // Calculate average order value
        const totalRevenue = completedOrders.reduce((total, order) => total + order.totalAmount, 0);
        const averageOrderValue = completedOrders.length > 0 ? totalRevenue / completedOrders.length : 0;

        // Calculate today's revenue
        const todayRevenue = todayOrders
            .filter(order => order.status === 'completed')
            .reduce((total, order) => total + order.totalAmount, 0);

        // Calculate most popular items
        const itemCounts = {};
        orders.forEach(order => {
            order.items.forEach(item => {
                if (itemCounts[item.label]) {
                    itemCounts[item.label] += item.quantity;
                } else {
                    itemCounts[item.label] = item.quantity;
                }
            });
        });

        const popularItems = Object.entries(itemCounts)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 3)
            .map(([name, count]) => ({ name, count }));

        // Calculate comprehensive customer analytics
        const customerStats = {};

        orders.forEach(order => {
            if (order.customerEmail) {
                if (customerStats[order.customerEmail]) {
                    customerStats[order.customerEmail].orders += 1;
                    customerStats[order.customerEmail].totalSpent += order.totalAmount;
                    customerStats[order.customerEmail].name = order.customerName || 'Unknown';
                } else {
                    customerStats[order.customerEmail] = {
                        orders: 1,
                        totalSpent: order.totalAmount,
                        name: order.customerName || 'Unknown',
                        email: order.customerEmail
                    };
                }
            }
        });

        // Calculate customer score (combines order frequency and spending)
        const topCustomers = Object.values(customerStats)
            .map(customer => ({
                name: customer.name,
                email: customer.email,
                orders: customer.orders,
                totalSpent: customer.totalSpent,
                averageOrderValue: customer.totalSpent / customer.orders
            }))
            .sort((a, b) => {
                // Sort by a combination of total spent and order frequency
                const scoreA = a.totalSpent * Math.log(a.orders + 1);
                const scoreB = b.totalSpent * Math.log(b.orders + 1);
                return scoreB - scoreA;
            })
            .slice(0, 5); // Show top 5 instead of 3

        // Calculate order success rate
        const totalOrders = orders.length;
        const completedCount = completedOrders.length;
        const successRate = totalOrders > 0 ? (completedCount / totalOrders) * 100 : 0;

        // Calculate cancellation rate
        const cancelledCount = orders.filter(order => order.status === 'cancelled').length;
        const cancellationRate = totalOrders > 0 ? (cancelledCount / totalOrders) * 100 : 0;

        // Calculate revenue growth (comparing last 30 days vs previous 30 days)
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const sixtyDaysAgo = new Date(today);
        sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

        const last30DaysRevenue = orders
            .filter(order => order.status === 'completed' && new Date(order.createdAt) >= thirtyDaysAgo)
            .reduce((total, order) => total + order.totalAmount, 0);

        const previous30DaysRevenue = orders
            .filter(order => order.status === 'completed' &&
                           new Date(order.createdAt) >= sixtyDaysAgo &&
                           new Date(order.createdAt) < thirtyDaysAgo)
            .reduce((total, order) => total + order.totalAmount, 0);

        const revenueGrowth = previous30DaysRevenue > 0 ?
            ((last30DaysRevenue - previous30DaysRevenue) / previous30DaysRevenue) * 100 : 0;

        const stats = {
            total: orders.length,
            pending: orders.filter(order => order.status === 'pending').length,
            inProgress: orders.filter(order => ['paid', 'preparing'].includes(order.status)).length,
            ready: orders.filter(order => order.status === 'ready').length,
            completed: todayOrders.filter(order => order.status === 'completed').length,
            totalCompleted: completedOrders.length,
            cancelled: cancelledCount,
            todayOrders: todayOrders.length,
            totalRevenue: totalRevenue,
            averageOrderValue: averageOrderValue,
            todayRevenue: todayRevenue,
            popularItems: popularItems,
            successRate: successRate,
            cancellationRate: cancellationRate,
            revenueGrowth: revenueGrowth,
            topCustomers: topCustomers
        };

        return stats;
    };

    // Filter and sort orders
    const getFilteredAndSortedOrders = () => {
        let filtered = [...orders];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(order =>
                order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.customerPhone?.includes(searchTerm) ||
                order._id.toString().includes(searchTerm)
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(order => order.status === statusFilter);
        }

        // Date filter
        if (dateFilter !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            switch (dateFilter) {
                case 'today':
                    filtered = filtered.filter(order => new Date(order.createdAt) >= today);
                    break;
                case 'yesterday':
                    const yesterday = new Date(today);
                    yesterday.setDate(yesterday.getDate() - 1);
                    filtered = filtered.filter(order => {
                        const orderDate = new Date(order.createdAt);
                        return orderDate >= yesterday && orderDate < today;
                    });
                    break;
                case 'week':
                    const weekAgo = new Date(today);
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    filtered = filtered.filter(order => new Date(order.createdAt) >= weekAgo);
                    break;
                case 'month':
                    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                    filtered = filtered.filter(order => new Date(order.createdAt) >= startOfMonth);
                    break;
                case 'lastmonth':
                    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    const startOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                    filtered = filtered.filter(order => {
                        const orderDate = new Date(order.createdAt);
                        return orderDate >= startOfLastMonth && orderDate < startOfCurrentMonth;
                    });
                    break;
                case '3months':
                    const threeMonthsAgo = new Date(today);
                    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
                    filtered = filtered.filter(order => new Date(order.createdAt) >= threeMonthsAgo);
                    break;
                case '6months':
                    const sixMonthsAgo = new Date(today);
                    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                    filtered = filtered.filter(order => new Date(order.createdAt) >= sixMonthsAgo);
                    break;
                case 'year':
                    const oneYearAgo = new Date(today);
                    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                    filtered = filtered.filter(order => new Date(order.createdAt) >= oneYearAgo);
                    break;
                case '3years':
                    const threeYearsAgo = new Date(today);
                    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
                    filtered = filtered.filter(order => new Date(order.createdAt) >= threeYearsAgo);
                    break;
                case '5years':
                    const fiveYearsAgo = new Date(today);
                    fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
                    filtered = filtered.filter(order => new Date(order.createdAt) >= fiveYearsAgo);
                    break;
                default:
                    break;
            }
        }

        // Sort orders
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.createdAt) - new Date(a.createdAt);
                case 'oldest':
                    return new Date(a.createdAt) - new Date(b.createdAt);
                case 'amount-high':
                    return b.totalAmount - a.totalAmount;
                case 'amount-low':
                    return a.totalAmount - b.totalAmount;
                case 'status':
                    return a.status.localeCompare(b.status);
                default:
                    return new Date(b.createdAt) - new Date(a.createdAt);
            }
        });

        return filtered;
    };

    // Export orders to CSV
    const exportToCSV = () => {
        const filteredOrders = getFilteredAndSortedOrders();
        const csvContent = [
            ['Order ID', 'Customer Name', 'Email', 'Phone', 'Status', 'Total Amount', 'GST', 'Items Count', 'Created At'],
            ...filteredOrders.map(order => [
                order._id.toString().slice(-6),
                order.customerName || '',
                order.customerEmail || '',
                order.customerPhone || '',
                order.status,
                order.totalAmount.toFixed(2),
                order.gstTax.toFixed(2),
                order.items.length,
                new Date(order.createdAt).toLocaleDateString()
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Stats Card Component
    const StatsCard = ({ title, value, icon: Icon, color = 'amber', subtitle = null }) => {
        const colorClasses = {
            amber: 'bg-gradient-to-r from-amber-500 to-orange-500',
            blue: 'bg-gradient-to-r from-blue-500 to-indigo-500',
            green: 'bg-gradient-to-r from-green-500 to-emerald-500',
            red: 'bg-gradient-to-r from-red-500 to-pink-500',
            purple: 'bg-gradient-to-r from-purple-500 to-violet-500',
            gray: 'bg-gradient-to-r from-gray-500 to-slate-500'
        };

        return (
            <div className="bg-white rounded-xl shadow-md p-6 border border-amber-100">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-amber-600 mb-1">{title}</p>
                        <p className="text-3xl font-bold text-amber-900">{value}</p>
                        {subtitle && (
                            <p className="text-xs text-amber-500 mt-1">{subtitle}</p>
                        )}
                    </div>
                    <div className={`p-3 rounded-full ${colorClasses[color]} text-white`}>
                        <Icon size={24} />
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-6">
                <div className="w-6 h-6 border-2 rounded-full border-amber-600 animate-spin border-t-transparent"></div>
            </div>
        );
    }

    const stats = getOrderStats();

    return (
        <>
            <div className="space-y-6">
                {/* Categorized Stats Cards */}
                <div className="space-y-8">
                    {/* Order Status Overview */}
                    <div>
                        <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center">
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            Order Status Overview
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                            <StatsCard
                                title="Total Orders"
                                value={stats.total}
                                icon={ShoppingCart}
                                color="amber"
                            />
                            <StatsCard
                                title="Pending Orders"
                                value={stats.pending}
                                icon={AlertCircle}
                                color="gray"
                            />
                            <StatsCard
                                title="In Progress"
                                value={stats.inProgress}
                                icon={CookingPot}
                                color="blue"
                            />
                            <StatsCard
                                title="Ready for Pickup"
                                value={stats.ready}
                                icon={CheckCircle}
                                color="green"
                            />
                            <StatsCard
                                title="Total Completed"
                                value={stats.totalCompleted}
                                icon={CheckCircle}
                                color="purple"
                            />
                        </div>
                    </div>

                    {/* Today's Performance */}
                    <div>
                        <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center">
                            <Clock className="w-5 h-5 mr-2" />
                            Today's Performance
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <StatsCard
                                title="Today's Orders"
                                value={stats.todayOrders}
                                icon={Clock}
                                color="blue"
                                subtitle="Orders received today"
                            />
                            <StatsCard
                                title="Completed Today"
                                value={stats.completed}
                                icon={CheckCircle}
                                color="purple"
                            />
                            <StatsCard
                                title="Today's Revenue"
                                value={`$${stats.todayRevenue.toFixed(2)}`}
                                icon={TrendingUp}
                                color="indigo"
                                subtitle="Revenue today"
                            />
                        </div>
                    </div>

                    {/* Revenue Analytics */}
                    <div>
                        <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center">
                            <DollarSign className="w-5 h-5 mr-2" />
                            Revenue Analytics
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <StatsCard
                                title="Total Revenue"
                                value={`$${stats.totalRevenue.toFixed(2)}`}
                                icon={TrendingUp}
                                color="green"
                                subtitle="From completed orders"
                            />
                            <StatsCard
                                title="Avg Order Value"
                                value={`$${stats.averageOrderValue.toFixed(2)}`}
                                icon={DollarSign}
                                color="emerald"
                                subtitle="Average per order"
                            />
                            <StatsCard
                                title="Revenue Growth"
                                value={`${stats.revenueGrowth >= 0 ? '+' : ''}${stats.revenueGrowth.toFixed(1)}%`}
                                icon={TrendingUp}
                                color={stats.revenueGrowth >= 0 ? "green" : "red"}
                                subtitle="Last 30 days vs previous"
                            />
                        </div>
                    </div>

                    {/* Performance Rates */}
                    <div>
                        <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center">
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Performance Rates
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <StatsCard
                                title="Success Rate"
                                value={`${stats.successRate.toFixed(1)}%`}
                                icon={CheckCircle}
                                color="green"
                                subtitle="Orders completed"
                            />
                            <StatsCard
                                title="Cancellation Rate"
                                value={`${stats.cancellationRate.toFixed(1)}%`}
                                icon={XCircle}
                                color="red"
                                subtitle="Orders cancelled"
                            />
                            <StatsCard
                                title="Cancelled Orders"
                                value={stats.cancelled}
                                icon={XCircle}
                                color="red"
                            />
                        </div>
                    </div>
                </div>

                {/* Popular Items Section */}
                {stats.popularItems.length > 0 && (
                    <div className="mt-6 bg-white rounded-xl shadow-md p-6 border border-amber-100">
                        <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center">
                            <Package className="w-5 h-5 mr-2" />
                            Top Selling Items
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {stats.popularItems.map((item, index) => (
                                <div key={item.name} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                                    <div className="flex items-center">
                                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-500 text-white text-sm font-bold mr-3">
                                            {index + 1}
                                        </div>
                                        <span className="font-medium text-amber-900 truncate">{item.name}</span>
                                    </div>
                                    <span className="text-amber-700 font-semibold">{item.count} sold</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Top Customers Section */}
                {stats.topCustomers.length > 0 && (
                    <div className="mt-6 space-y-6">
                        <div className="bg-white rounded-xl shadow-md p-6 border border-amber-100">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-semibold text-amber-900 flex items-center">
                                    <Users className="w-6 h-6 mr-3" />
                                    Top Customers
                                </h3>
                                <div className="text-sm text-amber-600">
                                    Based on spending & order frequency
                                </div>
                            </div>

                            {/* Top 3 Customers - Featured Layout */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                                {stats.topCustomers.slice(0, 3).map((customer, index) => (
                                    <div key={customer.email} className={`relative overflow-hidden rounded-xl border-2 ${
                                        index === 0 ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50' :
                                        index === 1 ? 'border-gray-300 bg-gradient-to-br from-gray-50 to-slate-50' :
                                        'border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50'
                                    }`}>
                                        {/* Rank Badge */}
                                        <div className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                            index === 0 ? 'bg-yellow-500' :
                                            index === 1 ? 'bg-gray-500' :
                                            'bg-amber-600'
                                        }`}>
                                            {index + 1}
                                        </div>

                                        {/* Trophy Icon for #1 */}
                                        {index === 0 && (
                                            <div className="absolute top-4 left-4 text-yellow-500">
                                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                            </div>
                                        )}

                                        <div className="p-6 pt-12">
                                            {/* Customer Avatar/Initials */}
                                            <div className="flex items-center mb-4">
                                                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                                                    index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                                                    index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-600' :
                                                    'bg-gradient-to-r from-amber-400 to-amber-600'
                                                }`}>
                                                    {customer.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="ml-3 flex-1 min-w-0">
                                                    <div className={`font-semibold truncate ${
                                                        index === 0 ? 'text-yellow-900' :
                                                        index === 1 ? 'text-gray-900' :
                                                        'text-amber-900'
                                                    }`}>
                                                        {customer.name}
                                                    </div>
                                                    <div className={`text-sm truncate ${
                                                        index === 0 ? 'text-yellow-700' :
                                                        index === 1 ? 'text-gray-700' :
                                                        'text-amber-700'
                                                    }`}>
                                                        {customer.email}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Stats Grid */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className={`text-center p-3 rounded-lg ${
                                                    index === 0 ? 'bg-yellow-100' :
                                                    index === 1 ? 'bg-gray-100' :
                                                    'bg-amber-100'
                                                }`}>
                                                    <div className={`text-2xl font-bold ${
                                                        index === 0 ? 'text-yellow-800' :
                                                        index === 1 ? 'text-gray-800' :
                                                        'text-amber-800'
                                                    }`}>
                                                        {customer.orders}
                                                    </div>
                                                    <div className={`text-xs font-medium ${
                                                        index === 0 ? 'text-yellow-600' :
                                                        index === 1 ? 'text-gray-600' :
                                                        'text-amber-600'
                                                    }`}>
                                                        Total Orders
                                                    </div>
                                                </div>

                                                <div className={`text-center p-3 rounded-lg ${
                                                    index === 0 ? 'bg-yellow-100' :
                                                    index === 1 ? 'bg-gray-100' :
                                                    'bg-amber-100'
                                                }`}>
                                                    <div className={`text-2xl font-bold ${
                                                        index === 0 ? 'text-yellow-800' :
                                                        index === 1 ? 'text-gray-800' :
                                                        'text-amber-800'
                                                    }`}>
                                                        ${customer.totalSpent.toFixed(0)}
                                                    </div>
                                                    <div className={`text-xs font-medium ${
                                                        index === 0 ? 'text-yellow-600' :
                                                        index === 1 ? 'text-gray-600' :
                                                        'text-amber-600'
                                                    }`}>
                                                        Total Spent
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Average Order Value */}
                                            <div className={`mt-4 p-3 rounded-lg text-center ${
                                                index === 0 ? 'bg-yellow-200' :
                                                index === 1 ? 'bg-gray-200' :
                                                'bg-amber-200'
                                            }`}>
                                                <div className={`text-lg font-bold ${
                                                    index === 0 ? 'text-yellow-900' :
                                                    index === 1 ? 'text-gray-900' :
                                                    'text-amber-900'
                                                }`}>
                                                    ${customer.averageOrderValue.toFixed(2)}
                                                </div>
                                                <div className={`text-xs font-medium ${
                                                    index === 0 ? 'text-yellow-700' :
                                                    index === 1 ? 'text-gray-700' :
                                                    'text-amber-700'
                                                }`}>
                                                    Average Order Value
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Remaining Top Customers - List View */}
                            {stats.topCustomers.length > 3 && (
                                <div>
                                    <h4 className="text-lg font-medium text-amber-900 mb-4">Other Top Customers</h4>
                                    <div className="space-y-3">
                                        {stats.topCustomers.slice(3).map((customer, index) => (
                                            <div key={customer.email} className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                                                <div className="flex items-center flex-1 min-w-0">
                                                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold mr-4">
                                                        {customer.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-purple-900 truncate">{customer.name}</div>
                                                        <div className="text-sm text-purple-600 truncate">{customer.email}</div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center space-x-6 text-sm">
                                                    <div className="text-center">
                                                        <div className="font-semibold text-purple-900">{customer.orders}</div>
                                                        <div className="text-purple-600">Orders</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="font-semibold text-purple-900">${customer.totalSpent.toFixed(0)}</div>
                                                        <div className="text-purple-600">Spent</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="font-semibold text-purple-900">${customer.averageOrderValue.toFixed(2)}</div>
                                                        <div className="text-purple-600">Avg</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Orders List */}
                <div className="p-6 bg-white shadow-md rounded-xl">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                        <h2 className="text-2xl font-semibold text-amber-900 font-bebas">Orders</h2>

                        {/* Export Button */}
                        <button
                            onClick={exportToCSV}
                            className="flex items-center px-4 py-2 text-sm font-medium text-white transition-colors bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Export CSV
                        </button>
                    </div>

                    {/* Filters and Search */}
                    <div className="mb-6 space-y-4">
                        {/* Search Bar */}
                        <div className="relative">
                            <Search className="absolute w-5 h-5 text-amber-400 left-3 top-3" />
                            <input
                                type="text"
                                placeholder="Search by order ID, customer name, email, or phone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            />
                        </div>

                        {/* Filter Controls */}
                        <div className="flex flex-wrap gap-4">
                            {/* Status Filter */}
                            <div className="relative">
                                <Filter className="absolute w-4 h-4 text-amber-400 left-3 top-3" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="pl-9 pr-4 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="pending">Pending</option>
                                    <option value="paid">Paid</option>
                                    <option value="preparing">Preparing</option>
                                    <option value="ready">Ready</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>

                            {/* Date Filter */}
                            <div className="relative">
                                <Calendar className="absolute w-4 h-4 text-amber-400 left-3 top-3" />
                                <select
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    className="pl-9 pr-4 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                >
                                    <option value="all">All Time</option>
                                    <option value="today">Today</option>
                                    <option value="yesterday">Yesterday</option>
                                    <option value="week">This Week</option>
                                    <option value="month">This Month</option>
                                    <option value="lastmonth">Last Month</option>
                                    <option value="3months">Last 3 Months</option>
                                    <option value="6months">Last 6 Months</option>
                                    <option value="year">Last Year</option>
                                    <option value="3years">Last 3 Years</option>
                                    <option value="5years">Last 5 Years</option>
                                </select>
                            </div>

                            {/* Sort Options */}
                            <div className="relative">
                                <User className="absolute w-4 h-4 text-amber-400 left-3 top-3" />
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="pl-9 pr-4 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                    <option value="amount-high">Highest Amount</option>
                                    <option value="amount-low">Lowest Amount</option>
                                    <option value="status">By Status</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {getFilteredAndSortedOrders().map((order) => (
                        <div
                            key={order._id}
                            className="overflow-hidden border rounded-lg border-amber-200"
                        >
                            {/* Order Header */}
                            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div>
                                            <h3 className="text-lg font-medium text-amber-900">
                                                Order #{order._id.toString().slice(-6)}
                                            </h3>
                                            <div className="flex items-center mt-1 text-sm text-amber-600">
                                                <Clock className="w-4 h-4 mr-1" />
                                                {formatDate(order.createdAt)}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-4">
                                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadgeColor(order.status)}`}>
                                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                        </span>
                                        <button
                                            onClick={() => setExpandedOrderId(expandedOrderId === order._id ? null : order._id)}
                                            className="p-1 rounded-full text-amber-600 hover:bg-amber-100"
                                        >
                                            {expandedOrderId === order._id ? (
                                                <ChevronUp className="w-5 h-5" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Order Details */}
                            {expandedOrderId === order._id && (
                                <div className="p-4 border-t border-amber-200">
                                    {/* Customer Information */}
                                    <div className="p-4 mb-4 rounded-lg bg-amber-50">
                                        <h4 className="mb-3 font-medium text-amber-900">Customer Details</h4>
                                        <div className="space-y-2">
                                            <p className="text-amber-800">{order.customerName}</p>
                                            <div className="flex items-center text-amber-600">
                                                <Phone className="w-4 h-4 mr-2" />
                                                <a href={`tel:${order.customerPhone}`} className="hover:text-amber-700">
                                                    {order.customerPhone}
                                                </a>
                                            </div>
                                            <div className="flex items-center text-amber-600">
                                                <Mail className="w-4 h-4 mr-2" />
                                                <a href={`mailto:${order.customerEmail}`} className="hover:text-amber-700">
                                                    {order.customerEmail}
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Order Items */}
                                    <div className="mb-4">
                                        <h4 className="mb-3 font-medium text-amber-900">Order Items</h4>
                                        <div className="space-y-2">
                                            {order.items.map((item, index) => (
                                                <div key={index} className="flex justify-between text-amber-800">
                                                    <span>{item.quantity}x {item.label}</span>
                                                    <span>${(item.price * item.quantity).toFixed(2)}</span>
                                                </div>
                                            ))}
                                            <div className="pt-2 mt-2 border-t border-amber-200">
                                                <div className="flex justify-between text-amber-600">
                                                    <span>GST (10%) </span>
                                                    <span>${order.gstTax.toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between font-medium text-amber-900">
                                                    <span>Total Amount</span>
                                                    <div className="flex items-center">
                                                        <DollarSign className="w-4 h-4 mr-1" />
                                                        {order.totalAmount.toFixed(2)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status Update */}
                                    <div className="pt-4 mt-4 border-t border-amber-200">
                                        <h4 className="mb-3 font-medium text-amber-900">Update Status</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {['preparing', 'ready', 'completed', 'cancelled'].map((status) => (
                                                <button
                                                    key={status}
                                                    onClick={() => handleStatusUpdateClick(order._id, status, order._id.toString().slice(-6))}
                                                    disabled={order.status === status}
                                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors
                ${order.status === status
                                                            ? 'bg-amber-100 text-amber-400 cursor-not-allowed'
                                                            : 'bg-amber-500 text-white hover:bg-amber-600'
                                                        }`}
                                                >
                                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                        {getFilteredAndSortedOrders().length === 0 && (
                            <div className="p-8 text-center rounded-lg text-amber-600 bg-amber-50">
                                {orders.length === 0 ? 'No orders found' : 'No orders match your current filters'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Add ConfirmDialog here, outside the main div */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() => !isUpdating && setConfirmDialog({ isOpen: false, orderId: null, newStatus: null })}
                onConfirm={handleStatusUpdate}
                title="Confirm Status Update"
                message={`Are you sure you want to change Order #${confirmDialog.orderNumber} status to ${confirmDialog.newStatus}?`}
                isDestructive={confirmDialog.newStatus === 'cancelled'}
                isLoading={isUpdating}
            />
        </>
    );
};

export default OrderManagement;